import { Component, Input, OnChanges } from '@angular/core';
import { ScaleType } from '@swimlane/ngx-charts';
import { coloreClasse, DistribuzioneStatistica, RISK_CLASSES, RiskClass } from '../../../../../models/rischio-analitico.model';

export interface OsservazioneClassificata {
  value: number;
  riskClass: RiskClass | null;
}

interface VoceLegenda {
  etichetta: string;
  colore: string;
}

@Component({
  selector: 'ebw-monorepo-rischio-analitico-distribuzione-chart',
  templateUrl: './rischio-analitico-distribuzione-chart.component.html',
  styleUrls: ['./rischio-analitico-distribuzione-chart.component.scss']
})
export class RischioAnaliticoDistribuzioneChartComponent implements OnChanges {
  @Input() distribuzione: DistribuzioneStatistica | null = null;
  @Input() unitaMisura = '';
  // misure con la classe di rischio di Kode (le stesse del trend): danno il colore alle barre
  @Input() osservazioni: OsservazioneClassificata[] = [];

  risultati: { name: string; value: number }[] = [];
  coloriBarre: { name: string; value: string }[] = [];
  legenda: VoceLegenda[] = [];

  colorScheme = { name: 'distribuzione', selectable: false, group: ScaleType.Ordinal, domain: ['#019bdb'] };

  ngOnChanges(): void {
    const istogramma = this.distribuzione?.istogramma ?? [];
    const classiPerBarra = istogramma.map((bin, i) => this._classePrevalente(bin.binMin, bin.binMax, i === istogramma.length - 1));

    this.risultati = istogramma.map((bin) => ({ name: `${this._formatta(bin.binMin)}–${this._formatta(bin.binMax)}`, value: bin.count }));
    this.coloriBarre = this.risultati.map((barra, i) => ({ name: barra.name, value: coloreClasse(classiPerBarra[i], 'last') }));
    this.legenda = this._vociLegenda(classiPerBarra);
  }

  formatta(valore: number): string {
    return this._formatta(valore);
  }

  // classe piu' frequente tra le misure che cadono nella fascia; a parita' vince la piu' grave.
  // L'ultima fascia include il suo estremo superiore (il valore massimo)
  private _classePrevalente(binMin: number, binMax: number, ultima: boolean): RiskClass | null {
    const conteggi = new Map<RiskClass, number>();
    this.osservazioni
      .filter((o) => o.riskClass && o.value >= binMin && (ultima ? o.value <= binMax : o.value < binMax))
      .forEach((o) => conteggi.set(o.riskClass, (conteggi.get(o.riskClass) ?? 0) + 1));

    let prevalente: RiskClass | null = null;
    conteggi.forEach((numero, classe) => {
      const numeroPrevalente = prevalente ? conteggi.get(prevalente) : 0;
      const piuGrave = prevalente !== null && RISK_CLASSES.indexOf(classe) > RISK_CLASSES.indexOf(prevalente);
      if (numero > numeroPrevalente || (numero === numeroPrevalente && piuGrave)) prevalente = classe;
    });
    return prevalente;
  }

  private _vociLegenda(classiPerBarra: (RiskClass | null)[]): VoceLegenda[] {
    const presenti = new Set(classiPerBarra);
    const voci: VoceLegenda[] = RISK_CLASSES.filter((classe) => presenti.has(classe)).map((classe) => ({
      etichetta: classe,
      colore: coloreClasse(classe, 'last')
    }));
    if (presenti.has(null)) voci.push({ etichetta: 'Classe non calcolata', colore: coloreClasse(null, 'last') });
    return voci;
  }

  private _formatta(valore: number): string {
    return Number.isInteger(valore) ? valore.toString() : valore.toFixed(2);
  }
}
